import { useState } from "react";
import {
  ratingOptions,
  regionOptions,
  yearOptions,
  type FilterState,
} from "@/lib/media";

interface FilterBarProps {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

interface GroupProps {
  label: string;
  options: { label: string; value: string }[];
  active: string;
  onSelect: (value: string) => void;
}

function FilterGroup({ label, options, active, onSelect }: GroupProps) {
  return (
    <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
      <span className="shrink-0 text-xs font-semibold text-foreground-500 lg:w-14">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className={`whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-colors cursor-pointer ${
              active === opt.value
                ? "bg-primary-500 text-background-50"
                : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-700"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ filters, onChange, onReset }: FilterBarProps) {
  const [open, setOpen] = useState(false);

  const regionOpts = regionOptions.map((r) => ({ label: r, value: r }));
  const yearOpts = yearOptions.map((y) => ({ label: y, value: y }));
  const ratingOpts = ratingOptions.map((r) => ({
    label: r.label,
    value: String(r.value),
  }));

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-4">
      <div className="flex items-center justify-between lg:hidden">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-foreground-800">
          <i className="ri-filter-3-line text-primary-500"></i>
          筛选条件
        </span>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 cursor-pointer"
        >
          {open ? "收起" : "展开"}
          <i className={open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>
      </div>

      <div className={`${open ? "mt-4 flex" : "hidden"} flex-col gap-4 lg:mt-0 lg:flex`}>
        <FilterGroup
          label="地区"
          options={regionOpts}
          active={filters.region}
          onSelect={(v) => onChange({ ...filters, region: v })}
        />
        <FilterGroup
          label="年份"
          options={yearOpts}
          active={filters.year}
          onSelect={(v) => onChange({ ...filters, year: v })}
        />
        <FilterGroup
          label="评分"
          options={ratingOpts}
          active={String(filters.minRating)}
          onSelect={(v) => onChange({ ...filters, minRating: Number(v) })}
        />
        <div className="flex justify-end pt-1">
          <button
            type="button"
            onClick={onReset}
            className="flex items-center gap-1 whitespace-nowrap rounded-md border border-background-200 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
          >
            <i className="ri-refresh-line"></i>
            重置筛选
          </button>
        </div>
      </div>
    </div>
  );
}